import { useEffect } from "react";

import { logAnalyticsEvent } from "@/constants/amplitude";
import { IS_PROD_BUILD } from "@/constants/env";
import { WebEventTypes } from "@/constants/event";
import { GAEvents, sendMetrics } from "@/constants/gtag";
import { PAYMENT_METHODS } from "@/constants/payment";
import { getPlanName, PricePlansIds } from "@/constants/prices";
import useAdParameters from "@/hooks/useAdParameters";
import { usePrefix } from "@/hooks/usePrefix";
import { useCurrency } from "@/utils/GeolocationProvider";
import { localStorageManager, LocalStorageManagerKeys } from "@/utils/localStorageManager";
import { trackersManager } from "@/utils/trackersManager";
import { PricePoint } from "@palta-brain/payments";

export const usePaymentComplete = ({
    ident,
    plan,
    isPaymentComplete,
    uuid,
    email,
    orderId,
    paymentMethod,
}: {
    ident: PricePlansIds | null | undefined;
    plan: PricePoint | null;
    isPaymentComplete?: boolean;
    uuid: string | null;
    email: string;
    orderId?: string | null;
    paymentMethod?: PAYMENT_METHODS;
}) => {
    const { currency } = useCurrency();
    const { getEventType } = usePrefix();
    const adParameters = useAdParameters();

    const screen = getEventType("paywall");

    useEffect(() => {
        const planName = getPlanName(ident);
        if (isPaymentComplete && plan) {
            const evtProps = {
                screen: screen,
                payment_system: paymentMethod,
                subDuration: planName,
                price: plan.introBasePrice,
                trial: false,
                intro_offer: false,
            };
            logAnalyticsEvent(WebEventTypes.PURCHASE_COMPLETED, {
                ...evtProps,
                subDuration: evtProps.subDuration,
                product_id: ident,
            });
            localStorageManager.storeItem(LocalStorageManagerKeys.IS_AUTH_STEP, true);
            localStorageManager.storeItem(LocalStorageManagerKeys.BOUGHT_PLAN_IDENT, ident);
            sendMetrics(GAEvents.Purchase, {
                clientId: uuid,
                orderId: orderId,
                email: email,
                currency: plan.currencyCode,
                price: plan.introBasePrice?.toString(),
                planId: ident,
                screen: screen,
                payment_system: paymentMethod,
                event_id: `LTV_${uuid}`,
            });
            if (IS_PROD_BUILD) {
                sendMetrics(WebEventTypes.PURCHASE_COMPLETED);
            }
            trackersManager.trackMarketingAttractionInfo({ uuid, adParameters, orderId });
        }
    }, [plan, orderId, isPaymentComplete, email, ident, paymentMethod, screen, uuid, currency]);
};
